import { makeStyles } from '@mui/styles';
import { Fragment } from 'react';
import {
    Show, TextField, TabbedShowLayout, Tab, Datagrid,
    ReferenceManyField, TopToolbar,
    RefreshButton, List, useShowContext, useTranslate,
    usePermissions, ReferenceField,
    useRecordContext, FunctionField, useShowController,
    ListContextProvider,
    useRefresh
}
    from 'react-admin';
import Button from '@mui/material/Button';
import { ShowIpAddress } from '../../components/ipAddressField';
import { Link, useLocation } from 'react-router-dom';
import {
    ControlVm, CreateInstanceTemplate, OpenGuacConsole,
    OpenVmConsole, RebootVm, StartAllVms, StopAllVms
} from '../vms/vmControl';
import VmCreate from '../workspaces/vmCreate';
import VolumeCreateInDialog from '../volumes/volumeCreateInDialog';
import { Tooltip, Typography } from '@mui/material';
import convertByte from '../../utils/convertByte';
import DeployInstanceTemplate from '../pods/instanceTemplateDeploy';
import BasicBreadcrumbs from '../../components/breadcrumb';
import { Visibility } from '@material-ui/icons';
import ShowRouterInterfaces from '../routers/routerInterfaceList';
import { StatusField } from '../../custom';
import determineSubnet from '../../components/determineSubnet';
import { useListContext } from 'react-admin';
import DeployFirewallTemplate from '../firewalls/firewallDeploy';
import { ResetSelection, useClearSelectionsOnChange } from '../../components';
import CustomBulkDeleteVMs from '../vms/customBulkDeleteVMs';

const useStyles = makeStyles((theme) => ({
    tableLastCell: {
        textAlign: 'right',
        padding: '6px 6px'
    },
    button: {
        minWidth: '10px',
        width: '36px',
        height: '32px'
    },
    room: {
        color: "rgba(0, 0, 0, 0.54)",
        fontWeight: "700",
        fontSize: "0.80em",
    },
    nameCell: {
        width: '150px',  // Adjust width for name column
        maxWidth: '150px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        font: 'inherit',
        color: theme.palette.primary.main,
        textDecoration: 'none'
    },
}));

const ListActionButtons = ({type, isRental}) => {
    const translate = useTranslate();
    const vm = useRecordContext();
    const location = useLocation();
    const classes = useStyles();
    const { permissions, isLoading } = usePermissions();

    if (isLoading) return <div>{translate('app.LoadingMessage')}</div>;

    const shouldBeDisabled = (permissions.length === 1 && permissions.includes('trainee')) && vm.type === 'firewall';
    const isTrainerOrTraineeOnly = permissions.every(p => ['trainer', 'trainee'].includes(p));

    const firewallPath = location.pathname.replace('/firewalls', '');
    const isTraineeOnly = (permissions.length === 1 && permissions.includes('trainee'));


    // If user is trainee and VM is firewall → hide all buttons
    if (isTraineeOnly && vm.type === 'firewall') return null;

    const showGuac = vm.guest_OS === 'windows';

    return (
        <Fragment>
            <span onClick={(event) => event.stopPropagation()}>
                <Tooltip title="Show" arrow placement="top">
                    <span style={{ width: '100%' }}>
                        <Button
                            size='large'
                            className={classes.button}
                            component={Link}
                            to={type === "firewall"
                                ? `${firewallPath}/instances/${vm.id}`
                                : location.pathname.includes('/instances')
                                    ? `${location.pathname}/${vm.id}` // Already in /instances
                                    : `${location.pathname}/instances/${vm.id}` // Add /instances if not present
                            }
                            startIcon={<Visibility />}
                            disabled={shouldBeDisabled}
                        ></Button>
                    </span>
                </Tooltip>
                <ControlVm action={translate('app.vms.action.start')} shouldBeDisabled={shouldBeDisabled} />
                <ControlVm action={translate('app.vms.action.stop')} shouldBeDisabled={shouldBeDisabled} />
                {!isTrainerOrTraineeOnly && (
                    <>
                        <ControlVm action={translate('app.vms.action.suspend')} shouldBeDisabled={shouldBeDisabled} />
                        <ControlVm action={translate('app.vms.action.resume')} shouldBeDisabled={shouldBeDisabled} />
                    </>
                )}
                <RebootVm shouldBeDisabled={shouldBeDisabled} />
                <OpenVmConsole shouldBeDisabled={shouldBeDisabled} />

                {showGuac ? <OpenGuacConsole shouldBeDisabled={shouldBeDisabled} /> : <OpenGuacConsole shouldBeHide={true} />}

                {(!isTrainerOrTraineeOnly) && <CreateInstanceTemplate shouldBeDisabled={shouldBeDisabled} />}
            </span>
        </Fragment>
    );
};

const VmBulkActionButtons = ({ permissions }) => {
    // Trainee only → hide delete
    const isTraineeOnly = permissions.length === 1 && permissions.includes('trainee');

    if (isTraineeOnly) return null;

    return (
        <Fragment>
            {/* <BulkDeleteWithConfirmButton /> */}
            <CustomBulkDeleteVMs />
        </Fragment>
    );
};


//Add deploy from instance template
const Actions = (props) => {
    const { permissions, isLoading } = usePermissions();
    const translate = useTranslate();

    if (isLoading) return <div>{translate('app.LoadingMessage')}</div>;

    // Check if user has specific roles
    const isAdmin = permissions.includes('admin');
    const isDesigner = permissions.includes('designer');
    const isTrainer = permissions.includes('trainer');
    const isTrainerOnly = permissions.length === 1 && permissions.includes('trainer');
    //const isTrainee = permissions.includes('trainee');

    // For admins and designers, there are no restrictions on the deployment
    const hasNoRestrictions = isAdmin || isDesigner;

    // Trainer with restriction (only when NOT admin/designer)
    const isRestrictedTrainer = isTrainer && !hasNoRestrictions;

    return (
        <TopToolbar>
            {/* If the user is not admin or designer, hide VmCreate */}
            {!hasNoRestrictions ? null : (
                <>
                    <VmCreate pod={props.data} />
                </>
            )}

            {/* If user is a trainer, show only the instance template linked to the training template */}
            {isRestrictedTrainer || hasNoRestrictions ? (
                <>
                    {/* If the user is a trainer, link the instance template to the training template */}
                    <DeployInstanceTemplate
                        pod={props.data}
                        isTrainer={isRestrictedTrainer ? true : null}
                    />
                    <StartAllVms pod={props.data} />
                    <StopAllVms pod={props.data} />
                </>
            ) : null}

            <RefreshButton />
        </TopToolbar>
    );
};

const FirewallActions = (props) => {
    const { permissions, isLoading } = usePermissions();
    const translate = useTranslate();

    if (isLoading) return <div>{translate('app.LoadingMessage')}</div>;

    // Check if user has specific roles
    const isAdmin = permissions.includes('admin');
    const isDesigner = permissions.includes('designer');
    const isTrainer = permissions.includes('trainer');
    //const isTrainee = permissions.includes('trainee');

    // For admins and designers, there are no restrictions on the deployment
    const hasNoRestrictions = isAdmin || isDesigner;

    return (
        <TopToolbar>
            {/* If the user is not admin or designer, hide VmCreate */}
            {!hasNoRestrictions ? null : (
                <>
                    <DeployFirewallTemplate
                        pod={props.data}
                        isTrainer={null}
                    />
                </>
            )}
            <RefreshButton />
        </TopToolbar>
    );
};

const VolumesActions = (props) => {
    const { permissions, isLoading: isLoadingRights } = usePermissions();

    const translate = useTranslate();
    const {
        error,  // error returned by dataProvider when it failed to fetch the record. 
        isLoading, // boolean that is true until the record is available for the first time
        record, // record fetched via dataProvider.getOne() based on the id from the location
    } = useShowContext();

    if (isLoadingRights) return <div>{translate('app.LoadingMessage')}</div>;

    if (isLoading) {
        return <div>{translate('app.LoadingMessage')}</div>;
    }
    if (error) {
        return <div>{translate('app.ErrorMessage')}</div>;
    }

    return (
        <TopToolbar>
            {!(permissions.length === 1 && permissions.includes('trainee')) ?
                <VolumeCreateInDialog pod={{ "id": record.id, "podName": record.name }} /> : null}
            <RefreshButton />
        </TopToolbar>
    )
};


const sortSubnets = ['Subnet1', 'Subnet2', 'Subnet3', 'Subnet0'];

const sortBySubnetThenName = (a, b) => {
    const getFirstSubnet = (record) => {
        const subnets = (record.netifs || [])
            .map(n => determineSubnet(n.addressIp))
            .filter(Boolean);
        return subnets[0] || '';
    };

    const aSubnet = getFirstSubnet(a);
    const bSubnet = getFirstSubnet(b);
    const aIndex = sortSubnets.indexOf(aSubnet);
    const bIndex = sortSubnets.indexOf(bSubnet);

    if (aIndex !== bIndex) return aIndex - bIndex;
    return a.name.localeCompare(b.name);
};

const ClearOnChange = () => {
    useClearSelectionsOnChange();
    return null;
};

const VMListSorted = ({ type, isRental, permissions }) => {
    const { data, isLoading, error, ...context } = useListContext();

    const translate = useTranslate();
    const classes = useStyles();

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error loading VMs</div>;
    if (!data) return <div>No VMs found</div>;

    // Add defensive check to ensure data is an object
    if (typeof data !== 'object') {
        console.error('Expected data to be an object, got:', typeof data, data);
        return <div>Invalid data format</div>;
    }

    // Check if data is empty
    const dataValues = Object.values(data);
    if (dataValues.length === 0) {
        return <div>No VMs found</div>;
    }

    const sortedArray = dataValues.sort(sortBySubnetThenName);
    const sortedData = sortedArray.reduce((acc, vm) => {
        acc[vm.id] = vm;
        return acc;
    }, {});
    const sortedIds = sortedArray.map(vm => vm.id);

    const sortedContext = {
        ...context,
        data: sortedData,
        ids: sortedIds,
        total: sortedIds.length,
        loaded: true,
    };

    const CustomEmpty = () => <div>{translate('app.emptyMessage')}</div>;

    const isAdminOrDesigner = permissions.includes('admin') || permissions.includes('designer');
    const isTrainerOnly = permissions.length === 1 && permissions.includes('trainer');
    const isTraineeOnly = permissions.length === 1 && permissions.includes('trainee');

    return (
        <ListContextProvider value={sortedContext}>
            <ResetSelection />
            <Datagrid
                data={sortedArray} // Pass array directly to Datagrid
                ids={sortedIds}
                bulkActionButtons={
                    type !== 'firewall'
                        ? <VmBulkActionButtons type={type} permissions={permissions} />
                        : false
                }
                empty={<CustomEmpty />}
            >
                <FunctionField
                    label="Name"
                    render={record => {
                        let linkPath;

                        if (isAdminOrDesigner) {
                            linkPath = `/vms/${record.id}/show`;
                        } else if (isTrainerOnly) {
                            // You need to have the current podId available
                            linkPath = `/myclassroom/Pod/${record.pod}/instances/${record.id}`;
                        } else if (isTraineeOnly) {
                            linkPath = `/mypod/instances/${record.id}`;
                        } else {
                            linkPath = '#'; // Fallback in case no role matches
                        }

                        return (
                            <Link to={linkPath} className={classes.nameCell}>
                                {record.name}
                            </Link>
                        );
                    }}
                />
                {!isTraineeOnly && !isRental && (
                    <TextField source="release" label={translate('app.vms.show.release')} />
                )}
                {isAdminOrDesigner && (
                    // <TextField source="description" label="Description" />
                    <FunctionField
                        label="Description"
                        render={record => {
                            const text = record.description || '-';
                            const short = text.length > 30 ? text.slice(0, 30) + '…' : text; // show first 30 chars

                            return (
                                <Tooltip
                                    title={
                                        text.length > 30 ? (
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                                                {text}
                                            </Typography>
                                        ) : ''
                                    }
                                    arrow
                                >
                                    <span>{short}</span>
                                </Tooltip>
                            );
                        }}
                        cellClassName={classes.descriptionCell}
                    />

                )}
                {!isTraineeOnly && !isRental && (
                    <ReferenceField source="flavor" reference="flavors" link="show" label="Flavor">
                        <FunctionField label="Name" render={record => record.name} />
                    </ReferenceField>
                )}
                {!isTraineeOnly && !isRental && (
                    <TextField source="guest_OS" label="Guest OS" />
                )}
                {/* Training Template display based on permissions */}
                <FunctionField
                    label={translate('app.vms.show.trainingTemplate')}
                    render={record => {
                        if (!record.trainingTemplate) return '-';

                        return (
                            <ReferenceField
                                source="trainingTemplate"
                                reference="trainingTemplates"
                                link={isAdminOrDesigner ? 'show' : false}
                                record={record}
                            >
                                <FunctionField
                                    render={choice =>
                                        choice?.name
                                            ? `${choice.name} #${choice.language} #${choice.step}`
                                            : '-'
                                    }
                                />
                            </ReferenceField>
                        );
                    }}
                />
                <FunctionField
                    label="Network"
                    render={record => {
                        if (!record.netifs || record.netifs.length === 0) return '';
                        const subnets = record.netifs
                            .map(netif => determineSubnet(netif.addressIp))
                            .filter(Boolean);
                        return [...new Set(subnets)].join(', ');
                    }}
                />
                <ShowIpAddress label="IP Address" />
                <StatusField source="osServerStatus" label="Status" />
                <FunctionField
                    label=""
                    render={record => <ListActionButtons type={type} isRental={isRental} record={record} />}
                    cellClassName={classes.tableLastCell}
                />
            </Datagrid>
        </ListContextProvider>
    );
};

const InstanceShowLayout = ({ type, isRental }) => {
    const {
        error,  // error returned by dataProvider when it failed to fetch the record. 
        isLoading, // boolean that is true until the record is available for the first time
        record, // record fetched via dataProvider.getOne() based on the id from the location
    } = useShowContext();

    const translate = useTranslate();
    const { permissions, isLoading: isLoadingPermissions } = usePermissions();

    if (isLoadingPermissions) return <div>{translate('app.LoadingMessage')}</div>;

    if (isLoading) {
        return <div>{translate('app.LoadingMessage')}</div>;
    }
    if (error) {
        return <div>{translate('app.ErrorMessage')}</div>;
    }


    const FlavorField = (record) => {
        return (
            <Tooltip componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'white',
                        color: '#6b489d',
                        fontSize: '1em',
                        padding: '6px 16px',
                        border: '2px solid #dadde9',
                    },
                },
            }} title=
                {
                    <span>
                        <strong>{record.name}</strong>
                        <div> <li> vcpus:{record.vcpus} </li> </div>
                        <div> <li> ram:{convertByte(record.ram, 2, "MB")} </li> </div>
                        <div> <li> size:{record.disk}GB </li>  </div>
                    </span>
                }>
                <span> <strong> {record.name} </strong> </span>
            </Tooltip >
        );
    };

    let customFilter = type === 'vm'
        ? { type: { $ne: "firewall" } }
        : { type: { $eq: "firewall" } };

    return (
        <>
            <List
                actions={record && type !== "firewall"
                    ? <Actions data={{ id: record.id, podName: record.name, type: record.type, room: record.room }} />
                    : record && type === "firewall"
                        ? <FirewallActions data={{ id: record.id, podName: record.name, type: record.type, room: record.room }} />
                        : null}
                pagination={false}>
                <ReferenceManyField
                    label={false}
                    perPage={25}
                    reference="vms"
                    target="pod"
                    filter={customFilter}
                >
                    <VMListSorted type={type} isRental={isRental} permissions={permissions} />
                </ReferenceManyField>
            </List >
        </>
    );
};

const extractClassName = (name) => {
    const match = name.match(/Pod\s+(\d{3})/);
    return match ? `C${parseInt(match[1], 10)}` : null;
};

const PodShow = ({ ...props }) => {
    const translate = useTranslate();
    const CustomEmpty = () => <div>{translate('app.emptyMessage')}</div>;
    const { permissions, isLoading: isLoadingRights } = usePermissions();
    const { error, isLoading, record } = useShowController({ resource: 'pods', id: props.id });
    let syncWithLocationValue = true;
    const refresh = useRefresh();
    //const CustomPagination = () => <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />;

    const refreshState = () => {
        refresh();
    };

    if (isLoadingRights) return <div>{translate('app.LoadingMessage')}</div>;

    if (isLoading) return <div>{translate('app.LoadingMessage')}</div>;

    if (error) return <div>{translate('app.ErrorMessage')}</div>;

    const isTraineeOnly = permissions.length === 1 && permissions.includes('trainee');

    const isAdminOrDesigner = permissions.includes('admin') || permissions.includes('designer');

    if (props.inClassroom) {
        syncWithLocationValue = false;
    }

    // Dedicated Rental classrooms 
    const rentalRooms = new Set(['C50', 'C51', 'C52', 'C53', 'C54', 'C55', 'C56']);

    const isRental = rentalRooms.has(extractClassName(record?.name));

    const shouldHideForRental = (isRental && !isAdminOrDesigner);


    return (
        <>
            {props.inClassroom ? '' : <BasicBreadcrumbs name={record.name} />}

            <Show {...props}>
                <TabbedShowLayout syncWithLocation={syncWithLocationValue}>

                    {(isTraineeOnly) &&
                        <Tab label={translate('app.pods.general')} >
                            {/* Pod Name */}
                            <TextField source="name" label={translate('app.pods.name')} />

                            {/* Room Name */}
                            <ReferenceField
                                label={translate('app.pods.room')}
                                source="room"
                                reference="classrooms"
                                link={false}
                            >
                                <TextField source="name" />
                            </ReferenceField>

                            {/* Training Name */}
                            <ReferenceField
                                label={translate('app.pods.training')}
                                source="room"
                                reference="classrooms"
                                link={false}
                            >
                                <ReferenceField
                                    source="training"
                                    reference="sessions"
                                    link={false}
                                >
                                    <TextField source="name" />
                                </ReferenceField>
                            </ReferenceField>

                            {/* Locator */}
                            <ReferenceField
                                label="Locator"
                                source="room"
                                reference="classrooms"
                                link={false}
                            >
                                <ReferenceField
                                    source="training"
                                    reference="sessions"
                                    link={false}
                                >
                                    <TextField source="locator" />
                                </ReferenceField>
                            </ReferenceField>

                        </Tab>
                    }

                    <Tab
                        label={translate('app.pods.instances')}
                        path={isTraineeOnly ? 'instances' : undefined} // Only define path for trainees
                    >
                        <InstanceShowLayout type='vm' isRental={shouldHideForRental} />
                    </Tab>

                    {!(
                        (permissions.length === 1 && permissions.includes('trainee')) ||
                        shouldHideForRental
                    ) ?
                        <Tab label={translate('app.pods.networks')} path='networks' >
                            <ReferenceManyField
                                label={false}
                                reference="networks"
                                target="pod"
                            >
                                <ResetSelection />
                                <Datagrid bulkActionButtons={false}>
                                    <TextField source="name" label={translate('app.pods.name')} />
                                    <TextField source="description" label={translate('app.pods.description')} />
                                    {!isTraineeOnly ?
                                        <TextField source="segmentationId" label={translate('app.networks.vlan')} />
                                        : null}
                                    <TextField source="cidr" />
                                    <TextField source="osNetworkStatus" label={translate('app.pods.status')} />
                                </Datagrid>
                            </ReferenceManyField>

                        </Tab>
                        : null
                    }

                    <Tab label={translate('app.pods.routers')} path='routers' >
                        <ReferenceManyField
                            label={false}
                            reference="routers"
                            target="pod"
                        >
                            <Datagrid empty={<CustomEmpty />} bulkActionButtons={false}>
                                <TextField source="name" label={translate('app.pods.name')} />
                                <TextField source="description" label={translate('app.pods.description')} />
                                <TextField source="osRouterStatus" label={translate('app.routers.status')} />
                                <ShowRouterInterfaces label='Interfaces' onSuccess={refreshState} />
                            </Datagrid>
                        </ReferenceManyField>
                    </Tab>

                    {!(
                        (permissions.length === 1 && permissions.includes('trainee')) ||
                        shouldHideForRental
                    ) ?
                        <Tab label={translate('app.pods.volumes')} path='volumes'>
                            <List actions={<VolumesActions />} pagination={false} >

                                <ReferenceManyField
                                    label={false}
                                    reference="volumes"
                                    target="pod"
                                    perPage={25}
                                >
                                    <Datagrid empty={<CustomEmpty />} bulkActionButtons={false}>
                                        <TextField source="name" label={translate('app.pods.name')} />
                                        <TextField source="description" label={translate('app.pods.description')} />
                                        <TextField source="device" label={translate('app.vms.show.attachedTo')} />
                                        <TextField source="size" label={translate('app.pods.size')} />
                                        <TextField source="osVolumeStatus" label={translate('app.pods.status')} />
                                    </Datagrid>
                                </ReferenceManyField>
                            </List>
                        </Tab> : null
                    }

                    <Tab label={translate('app.pods.firewalls')} path='firewalls'>
                        <InstanceShowLayout type='firewall' isRental={shouldHideForRental} />
                    </Tab>

                </TabbedShowLayout>

            </Show >
        </>
    );
};

export default PodShow;